import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { JOB_LABEL, cn, isOnline, isStaffShown, presenceOf } from './model/derive';
import { useWorkboardCtx } from './model/useWorkboard';
import { StatusChip } from './primitives';
import { hoursKst } from './MemberCard';

/**
 * 받는 사람 선택기 (Question 댓글) — 행마다 상태 점 + 이름·직무 + 작업 시간(KST), 스태프는 Staff 배지.
 * 이탈자·본인·팀장은 빠진다. 팀원 카드는 띄우지 않는다.
 */
const PeoplePicker = ({ value, onChange, className }) => {
  const { data, memberById, now } = useWorkboardCtx();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const people = [...memberById.values()].filter(
    (m) => m.role !== 'npc' && m.id !== data.viewerId && presenceOf(m, now) !== 'dropped',
  );
  const selected = memberById.get(value);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const pick = (m) => {
    onChange(m.id);
    setOpen(false);
  };
  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'Escape') setOpen(false);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, people.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && people[active]) {
      e.preventDefault();
      pick(people[active]);
    }
  };

  return (
    <div ref={ref} className='relative'>
      <button
        type='button'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKey}
        className={cn(
          'flex h-8 min-w-[160px] items-center justify-between gap-2 rounded-lg border border-green-deep03/20 bg-grayscale-gray00 px-2.5 text-[13px] text-text-primary',
          className,
        )}
      >
        <span className={cn('truncate', !selected && 'text-text-placeholder')}>
          {selected ? selected.name : 'Select a person'}
        </span>
        <ChevronIcon className='h-4 w-4 shrink-0 text-icon-secondary' />
      </button>
      {open && (
        <ul
          role='listbox'
          aria-label='Recipient'
          className='absolute left-0 top-9 z-30 max-h-[280px] w-[300px] overflow-y-auto rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1 shadow-4'
        >
          {people.length === 0 && (
            <li className='px-3 py-3 text-[13px] text-text-secondary'>No one to ask yet.</li>
          )}
          {people.map((m, i) => {
            const online = isOnline(m, now, data.viewerId); // 점 = 실제 접속
            return (
              <li
                key={m.id}
                role='option'
                aria-selected={m.id === value}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                className={cn(
                  'flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2',
                  (i === active || m.id === value) && 'bg-surface-green10',
                )}
              >
                <span
                  aria-label={online ? 'Online' : 'Offline'}
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    online ? 'bg-functional-success03' : 'bg-grayscale-gray04',
                  )}
                />
                <span className='flex min-w-0 flex-1 flex-col'>
                  <span className='flex min-w-0 items-center gap-1.5 text-[13px] text-text-primary'>
                    <span className='truncate font-medium'>{m.name}</span>
                    <span className='shrink-0 text-text-secondary'>
                      · {JOB_LABEL[m.job] ?? m.job}
                    </span>
                    {isStaffShown(m) && <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />}
                  </span>
                  <span className='text-[12px] text-text-secondary'>{hoursKst(m, now)}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PeoplePicker;
